import { useState, useEffect, useMemo } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../config/firebase";
import { Loader2, AlertCircle, Lock } from "lucide-react";

function CheckoutForm({ onSuccess, amount }) {
  const stripe   = useStripe();
  const elements = useElements();
  const [error,   setError]   = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);
    setError("");
    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
    });
    if (stripeError) {
      setError(stripeError.message || "Payment failed.");
      setLoading(false);
    } else {
      onSuccess(paymentIntent?.id);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          <AlertCircle size={14} /> {error}
        </div>
      )}
      <button
        type="submit"
        disabled={!stripe || loading}
        className="btn-primary w-full flex items-center justify-center gap-2"
      >
        {loading ? (
          <><Loader2 size={16} className="animate-spin" /> Processing...</>
        ) : (
          <><Lock size={14} /> Pay ${amount}</>
        )}
      </button>
      <p className="text-center text-ink-400 text-xs font-sans flex items-center justify-center gap-1">
        <Lock size={10} /> Secured by Stripe
      </p>
    </form>
  );
}

export default function PaymentStep({ booking, tenantId, onSuccess }) {
  const [clientSecret,       setClientSecret]       = useState(null);
  const [connectedAccountId, setConnectedAccountId] = useState(null);
  const [error,              setError]              = useState("");

  useEffect(() => {
    if (!booking.service?.price || !tenantId) return;
    const fn = httpsCallable(functions, "createBookingPaymentIntent");
    fn({
      tenantId,
      serviceId:    booking.service.id || "",
      serviceName:  booking.service.name,
      servicePrice: booking.service.price,
    })
      .then(({ data }) => {
        setClientSecret(data.clientSecret);
        setConnectedAccountId(data.connectedAccountId);
      })
      .catch(err => setError(err.message || "Could not initialise payment."));
  }, [tenantId, booking.service]);

  // Direct charges: the PaymentIntent lives on the vendor's connected account,
  // so Stripe.js must be initialised scoped to that account to confirm it.
  const stripePromise = useMemo(() => {
    const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    if (!key || !connectedAccountId) return null;
    return loadStripe(key, { stripeAccount: connectedAccountId });
  }, [connectedAccountId]);

  if (error) {
    return (
      <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
        <AlertCircle size={14} /> {error}
      </div>
    );
  }

  if (!clientSecret || !stripePromise) {
    return (
      <div className="flex items-center justify-center py-12 gap-2 text-ink-400 text-sm font-sans">
        <Loader2 size={16} className="animate-spin" /> Preparing payment…
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto space-y-6">
      <div>
        <h2 className="font-semibold text-xl text-ink-900">Payment</h2>
        <p className="text-ink-400 text-xs font-sans mt-0.5">
          {booking.service?.name} · ${booking.service?.price}
        </p>
      </div>
      <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: "stripe" } }}>
        <CheckoutForm onSuccess={onSuccess} amount={booking.service?.price} />
      </Elements>
    </div>
  );
}
